import React, { useState, useMemo, useCallback } from 'react';
import loadable from '@loadable/component';
import { sortBy } from 'ramda';
import {
  RadioButton,
  ControlLink,
  Button,
  Stack,
  Text,
  Overlay,
  LoadingBalls,
  Input
} from '@moda/om';
import { useFeatureFlag } from '@moda/portal-stanchions';
import { RestAddress } from '../../../hooks/useAddress';
import { Box } from '../../Box';
import { FormButtonsLayout } from '../../FormButtonsLayout';
import { ShippingAddressPreview } from '../ShippingAddressPreview';
import { useUser } from '../../../hooks/useUser';
import { sendOTPCode } from '../../../lib/sendOTPCode';
import { verifyOTPCode } from '../../../lib/verifyOTPCode';
import './ShippingAddressList.scss';

const ShippingAddressValidationPopup = loadable(() => import('../ShippingAddressValidationPopup'), {
  resolveComponent: exports => exports.ShippingAddressValidationPopup
});

interface Props {
  addresses: RestAddress[];
  disabledAddressIds?: string[];
  defaultAddressId?: string;
  onAdd: () => void;
  onCancel: () => void;
  onChange: (addressId: string) => void;
  onEdit?: (addressId: string) => void;
}

enum Mode {
  Resting,
  Validating
}

export const ShippingAddressList: React.FC<Props> = ({
  addresses,
  disabledAddressIds,
  defaultAddressId,
  onAdd,
  onCancel,
  onChange,
  onEdit
}) => {
  const [mode, setMode] = useState(Mode.Resting);
  const [selectedAddressId, setSelectedAddressId] = useState<string | undefined>(defaultAddressId);
  const selectedAddress = addresses.find(address => address.id === selectedAddressId);

  const sortedAddresses = useMemo(
    () => sortBy(address => disabledAddressIds?.includes(address.id) ?? false, addresses),
    [addresses, disabledAddressIds]
  );
  const {
    user: { id, email, accessToken }
  } = useUser();

  const otpEnabled = useFeatureFlag('enable-otp', [{ default: false }, { if: true, then: true }]);

  const [showOTPInput, setShowOTPInput] = useState<boolean>(false);
  const [code, setCode] = useState<string>();

  const handleChange = useCallback(() => {
    if (id && otpEnabled) {
      setShowOTPInput(true);
      sendOTPCode(id, email || '', accessToken || '');
    } else {
      onAdd();
    }
  }, [id, otpEnabled, email, accessToken, onAdd]);

  const submitCode = useCallback(
    (code: string) => {
      verifyOTPCode(id || '', email || '', accessToken || '', code);
      onAdd();
    },
    [accessToken, email, id, onAdd]
  );

  return (
    <>
      <Stack className="ShippingAddressList" space={6}>
        <Stack space={4}>
          {sortedAddresses.map((address: RestAddress) => {
            const disabled = disabledAddressIds?.includes(address.id);

            return (
              <Box
                key={address.id}
                className="ShippingAddressList__option"
                tabIndex={0}
                role="button"
                onKeyDown={({ key }) =>
                  key === 'Enter' && !disabled && setSelectedAddressId(address.id)
                }
                onClick={() => !disabled && setSelectedAddressId(address.id)}
              >
                <RadioButton
                  disabled={disabled}
                  checked={address.id === selectedAddressId}
                  onChange={() => !disabled && setSelectedAddressId(address.id)}
                  value={address.id}
                />
                <div className="ShippingAddressList__option-main">
                  <Stack space={2}>
                    <ShippingAddressPreview attributes={address.attributes} />
                    {disabled && (
                      <Text color="code-red">
                        Some items can not be shipped to this address. Please edit or select another
                        address.
                      </Text>
                    )}
                  </Stack>
                  {onEdit && (
                    <ControlLink
                      className="ShippingAddressList__option-edit"
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        event.stopPropagation();
                        onEdit(address.id);
                      }}
                    >
                      Edit
                    </ControlLink>
                  )}
                </div>
              </Box>
            );
          })}
        </Stack>

        {showOTPInput && (
          <Stack className="ShippingAddressList__OTP" space={4}>
            <Input
              label="Enter code received"
              onChange={value => setCode(value)}
              name="otpcode"
              type="text"
              value={code}
            />
            <Button type="submit" onClick={() => code && submitCode(code.toString())}>
              Verify code
            </Button>
          </Stack>
        )}

        <ControlLink className="ShippingAddressList__add-button" onClick={handleChange}>
          Add New Address
        </ControlLink>

        <FormButtonsLayout>
          <Button data-test-id="shipping-address-list--cancel" onClick={onCancel} secondary>
            Cancel
          </Button>

          {selectedAddressId && (
            <Button
              data-test-id="shipping-address-list--continue"
              onClick={() => setMode(Mode.Validating)}
            >
              Continue
            </Button>
          )}
        </FormButtonsLayout>
      </Stack>

      {mode === Mode.Validating && selectedAddress && (
        <ShippingAddressValidationPopup
          attributes={selectedAddress.attributes}
          onSubmit={() => {
            setMode(Mode.Resting);
            if (selectedAddressId) onChange(selectedAddressId);
          }}
          onClose={() => setMode(Mode.Resting)}
        />
      )}

      <Overlay show={mode === Mode.Validating}>
        <LoadingBalls />
        <Text treatment="eyebrow">Saving</Text>
      </Overlay>
    </>
  );
};
